<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import EditPriceModal from '@/components/pageContent/modules/EditPriceModal.vue';
import MediaSliderModal from '@/components/pageContent/modules/MediaSliderModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import styles from './header-block-style.scss';

const b = bem('header-block', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'HeaderBlock',
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    EditButtonModal,
    EditPriceModal,
    MediaSliderModal,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update'],
  data() {
    return {
      currentSlideIndex: 0,
      // Modal visibility states
      titleModalVisible: false,
      subTitleModalVisible: false,
      descriptionModalVisible: false,
      buttonModalVisible: false,
      priceModalVisible: false,
      mediaModalVisible: false,
    };
  },
  computed: {
    title() {
      return this.data.title || '';
    },
    titleMobile() {
      return this.data.titleMobile || this.title;
    },
    subTitle() {
      return this.data.subTitle || '';
    },
    subTitleMobile() {
      return this.data.subTitleMobile || this.subTitle;
    },
    description() {
      return this.data.description || '';
    },
    descriptionMobile() {
      return this.data.descriptionMobile || this.description;
    },
    buttonText() {
      return this.data.buttonText || '';
    },
    buttonTextMobile() {
      return this.data.buttonTextMobile || this.buttonText;
    },
    buttonLink() {
      return this.data.buttonLink || '';
    },
    beforeText() {
      return this.data.beforeText || '';
    },
    beforeTextMobile() {
      return this.data.beforeTextMobile || this.beforeText;
    },
    afterText() {
      return this.data.afterText || '';
    },
    afterTextMobile() {
      return this.data.afterTextMobile || this.afterText;
    },
    price() {
      return this.data.price || 0;
    },
    additionalDiscount() {
      return this.data.additionalDiscount || 0;
    },
    isShowVideo() {
      return this.data.isShowVideo || false;
    },
    videoUrl() {
      return this.data.videoUrl || '';
    },
    images() {
      return this.data.blocks || this.data.images || [];
    },
    displayTitle() {
      return this.isMobileView ? this.titleMobile : this.title;
    },
    displaySubTitle() {
      return this.isMobileView ? this.subTitleMobile : this.subTitle;
    },
    displayDescription() {
      return this.isMobileView ? this.descriptionMobile : this.description;
    },
    displayButtonText() {
      return this.isMobileView ? this.buttonTextMobile : this.buttonText;
    },
    displayBeforeText() {
      return this.isMobileView ? this.beforeTextMobile : this.beforeText;
    },
    displayAfterText() {
      return this.isMobileView ? this.afterTextMobile : this.afterText;
    },
    currentSlide() {
      return this.images[this.currentSlideIndex] || {};
    },
    currentImage() {
      if (this.images.length > 0 && this.images[0]) {
        return this.images[0].imageUrl || '';
      }
      return '';
    },
    currentImageMobile() {
      if (this.images.length > 0 && this.images[0]) {
        return this.images[0].imageUrlMobile || '';
      }
      return '';
    },
    hasPrice() {
      return this.price > 0 || this.data.productId || this.data.optionId;
    },
    hasInfoBlock() {
      return this.displaySubTitle
        || this.displayDescription
        || this.displayButtonText
        || this.hasPrice
        || this.isEditMode;
    },
  },
  methods: {
    b(element, modifiers) {
      return b(element, modifiers);
    },
    // Modal open methods
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    openSubTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.subTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    openButtonModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.buttonModalVisible = true;
      }
    },
    openPriceModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.priceModalVisible = true;
      }
    },
    openMediaModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.mediaModalVisible = true;
      }
    },
    // Save handlers
    handleTitleSave(values) {
      this.$emit('update', { title: values.title, titleMobile: values.titleMobile });
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update', { title: '', titleMobile: '' });
      this.titleModalVisible = false;
    },
    handleSubTitleSave(values) {
      this.$emit('update', { subTitle: values.title, subTitleMobile: values.titleMobile });
      this.subTitleModalVisible = false;
    },
    handleSubTitleDelete() {
      this.$emit('update', { subTitle: '', subTitleMobile: '' });
      this.subTitleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', { description: '', descriptionMobile: '' });
      this.descriptionModalVisible = false;
    },
    handleButtonSave(values) {
      this.$emit('update', {
        buttonText: values.buttonText,
        buttonTextMobile: values.buttonTextMobile,
        buttonLink: values.buttonLink,
      });
      this.buttonModalVisible = false;
    },
    handleButtonDelete() {
      this.$emit('update', {
        buttonText: '',
        buttonTextMobile: '',
        buttonLink: '',
      });
      this.buttonModalVisible = false;
    },
    handlePriceSave(values) {
      this.$emit('update', {
        beforeText: values.beforeText,
        afterText: values.afterText,
        productId: values.productId,
        optionId: values.optionId,
        price: values.price,
        additionalDiscount: values.additionalDiscount,
      });
      this.priceModalVisible = false;
    },
    handlePriceDelete() {
      this.$emit('update', {
        beforeText: '',
        afterText: '',
        productId: 0,
        optionId: 0,
        price: 0,
        additionalDiscount: 0,
      });
      this.priceModalVisible = false;
    },
    handleMediaSave(result) {
      this.$emit('update', {
        blocks: result.blocks,
        isShowVideo: result.isShowVideo,
        videoUrl: result.videoUrl,
      });
      this.mediaModalVisible = false;
    },
  },
};
</script>

<template>
  <article :class="b('')">
    <div :class="b('container', { isEditMode, isMobileView })">
      <div
        :class="b('wrapper-image', { isEditMode, isMobileView, editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openMediaModal"
        @keyup.enter="openMediaModal"
        @keyup.space="openMediaModal"
      >
        <template v-if="isShowVideo && videoUrl">
          <video
            :class="b('video', { isEditMode, isMobileView })"
            :src="videoUrl"
            autoplay
            loop
            muted
            playsinline
          >
            <track kind="captions" />
          </video>
        </template>
        <template v-else-if="images && images.length > 0 && images[0].imageUrl">
          <img
            v-if="images.length === 1"
            :class="b('image', { isEditMode, isMobileView })"
            :src="isMobileView
              ? (images[0].imageUrlMobile || images[0].imageUrl)
              : images[0].imageUrl"
            alt="banner"
          />
          <div v-else :class="b('slider', { isEditMode, isMobileView })">
            <img
              :class="b('image', { isEditMode, isMobileView })"
              :src="isMobileView
                ? (currentSlide.imageUrlMobile || currentSlide.imageUrl)
                : currentSlide.imageUrl"
              alt="banner"
            />
          </div>
        </template>
        <div
          v-else
          :class="b('image-placeholder', { isEditMode, isMobileView })"
        >
          <span
            v-if="(!images || images?.length === 0) && isEditMode"
            :class="b('placeholder-plus')"
          >+</span>
          <span
            v-if="(!images || images?.length === 0) && isEditMode"
          >CLICK TO UPLOAD</span>
        </div>
      </div>
      <div
        :class="b('title-wrap', { editable: isEditMode, isMobileView })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openTitleModal"
        @keyup.enter="openTitleModal"
        @keyup.space="openTitleModal"
      >
        <h1 :class="b('title', { isMobileView })">
          {{ displayTitle || 'Default title' }}
        </h1>
        <PencilIcon v-if="isEditMode" :size="20" color="#eec586" :class="b('edit-icon')" />
      </div>
      <div v-if="!isMobileView" :class="b('fs-block')">
        <div :class="b('rate', { isMobileView })">
          <span :class="b('rate-stars')">★★★★★</span>
          <span :class="b('rate-divider')">|</span>
          <span :class="b('rate-text')">Two Decades of Perfect Reviews</span>
          <span :class="b('rate-divider')">|</span>
          <span :class="b('rate-link')">2717 reviews</span>
        </div>
      </div>
    </div>

    <div v-if="hasInfoBlock" :class="b('info-block', { isMobileView })">
      <!-- Subtitle -->
      <div
        v-if="displaySubTitle"
        :class="b('sub-title-wrap', { editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openSubTitleModal"
        @keyup.enter="openSubTitleModal"
        @keyup.space="openSubTitleModal"
      >
        <div :class="b('sub-title', { isMobileView })">{{ displaySubTitle }}</div>
        <PencilIcon v-if="isEditMode" :size="18" color="#5F8AA4" :class="b('edit-icon')" />
      </div>
      <div
        v-else-if="isEditMode"
        :class="b('add-field')"
        role="button"
        tabindex="0"
        @click="openSubTitleModal"
        @keyup.enter="openSubTitleModal"
        @keyup.space="openSubTitleModal"
      >
        + Add subtitle
      </div>

      <!-- Description -->
      <div
        v-if="displayDescription"
        :class="b('description-wrap', { editable: isEditMode, isMobileView })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openDescriptionModal"
        @keyup.enter="openDescriptionModal"
        @keyup.space="openDescriptionModal"
      >
        <div :class="b('description', { isMobileView })">{{ displayDescription }}</div>
        <PencilIcon v-if="isEditMode" :size="18" color="#293241" :class="b('edit-icon')" />
      </div>
      <div
        v-else-if="isEditMode"
        :class="b('add-field')"
        role="button"
        tabindex="0"
        @click="openDescriptionModal"
        @keyup.enter="openDescriptionModal"
        @keyup.space="openDescriptionModal"
      >
        + Add description
      </div>

      <!-- Button -->
      <div
        v-if="displayButtonText"
        :class="b('button-wrap', { editable: isEditMode, isMobileView })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openButtonModal"
        @keyup.enter="openButtonModal"
        @keyup.space="openButtonModal"
      >
        <div :class="b('button', { isMobileView })">
          {{ displayButtonText }}
          <PencilIcon
            v-if="isEditMode"
            :size="16"
            color="#293241"
            :class="b('edit-icon', { inline: true })"
          />
        </div>
      </div>
      <div
        v-else-if="isEditMode"
        :class="b('add-field')"
        role="button"
        tabindex="0"
        @click="openButtonModal"
        @keyup.enter="openButtonModal"
        @keyup.space="openButtonModal"
      >
        + Add button
      </div>

      <!-- Price -->
      <div
        v-if="hasPrice"
        :class="b('price-wrap', { editable: isEditMode })"
        :role="isEditMode ? 'button' : undefined"
        :tabindex="isEditMode ? 0 : undefined"
        @click="openPriceModal"
        @keyup.enter="openPriceModal"
        @keyup.space="openPriceModal"
      >
        <div :class="b('price-block', { isMobileView })">
          <span v-if="displayBeforeText" :class="b('price-before', { isMobileView })">
            {{ displayBeforeText }}
          </span>
          <template v-if="additionalDiscount">
            <span :class="b('price-old', { isMobileView })">${{ price }}</span>
            <span :class="b('price-new', { isMobileView })">${{ price - additionalDiscount }}</span>
          </template>
          <span v-else :class="b('price-value', { isMobileView })">${{ price }}</span>
          <span v-if="displayAfterText" :class="b('price-after', { isMobileView })">
            {{ displayAfterText }}
          </span>
        </div>
        <PencilIcon v-if="isEditMode" :size="16" color="#293241" :class="b('edit-icon')" />
      </div>
      <div
        v-else-if="isEditMode"
        :class="b('add-field')"
        role="button"
        tabindex="0"
        @click="openPriceModal"
        @keyup.enter="openPriceModal"
        @keyup.space="openPriceModal"
      >
        + Add price with product
      </div>
      <div v-if="isMobileView" :class="b('fs-block', { isMobile: true })">
        <div :class="b('rate', { isMobile: true })">
          <span :class="b('rate-text', { isMobile: true })">Two Decades of Trust & Excellence</span>
          <span :class="b('rate-stars', { isMobile: true })">★★★★★</span>
          <span :class="b('rate-link', { isMobile: true })">2717 reviews</span>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <EditTitleModal
      :visible="titleModalVisible"
      :title="title"
      :title-mobile="titleMobile"
      modal-title="Edit Title"
      @close="titleModalVisible = false"
      @save="handleTitleSave"
      @delete="handleTitleDelete"
    />

    <EditTitleModal
      :visible="subTitleModalVisible"
      :title="subTitle"
      :title-mobile="subTitleMobile"
      modal-title="Edit Subtitle"
      @close="subTitleModalVisible = false"
      @save="handleSubTitleSave"
      @delete="handleSubTitleDelete"
    />

    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <EditButtonModal
      :visible="buttonModalVisible"
      :button-text="buttonText"
      :button-text-mobile="buttonTextMobile"
      :button-link="buttonLink"
      @close="buttonModalVisible = false"
      @save="handleButtonSave"
      @delete="handleButtonDelete"
    />

    <EditPriceModal
      :visible="priceModalVisible"
      :before-text="beforeText"
      :after-text="afterText"
      :product-id="data.productId"
      :option-id="data.optionId"
      :show-delete="hasPrice"
      @close="priceModalVisible = false"
      @save="handlePriceSave"
      @delete="handlePriceDelete"
    />

    <MediaSliderModal
      :visible="mediaModalVisible"
      :slides="images"
      :is-show-video="isShowVideo"
      :video-url="videoUrl"
      @close="mediaModalVisible = false"
      @save="handleMediaSave"
    />
  </article>
</template>
